import { useCallback, useMemo } from 'react';

import { ViableAccountsType, useSelectPayoutAccount } from '@givebacks/api/core';
import { PAYMENT_ACCOUNTS_DICTIONARY } from '@givebacks/utils';
import { Alert, ButtonMantine, Card, Group, RadioMantine, Skeleton, Stack, Text, Title } from 'ui';
import { UseFormReturnType, isNotEmpty, useForm } from 'ui/Form';
import { useDebouncedValue } from 'ui/Hooks';

import { useFormListValidator } from '../../../../hooks';

type ChoosePaymentAccountForm = {
  paymentAccount?: ViableAccountsType;
};

type UseChoosePaymentAccountForm = UseFormReturnType<ChoosePaymentAccountForm>;

type ChoosePaymentAccountProps = {
  openCreateAccount: (account: ViableAccountsType) => void;
  onRemove?: (account: ViableAccountsType, accountId: string) => void;

  withTitle?: boolean;
};

interface PaymentCardProps extends Pick<ChoosePaymentAccountProps, 'openCreateAccount'> {
  account: keyof typeof PAYMENT_ACCOUNTS_DICTIONARY;
  onRemove?: (accountId: string) => void;
}

const PaymentName = ({ account }: Omit<PaymentCardProps, 'openCreateAccount'>) => {
  const { data: paymentAccount } = useSelectPayoutAccount(account);

  const name = useMemo(() => {
    switch (account) {
      case 'bank':
        return `${paymentAccount?.display} (${paymentAccount?.bank_account_purpose})`;
      case 'paypal':
      case 'venmo':
        return `@ ${paymentAccount?.display}`;

      default:
        return '';
    }
  }, [account, paymentAccount?.bank_account_purpose, paymentAccount?.display]);

  if (!paymentAccount) return null;
  return (
    <Text fz={10} c="gray">
      {name}
    </Text>
  );
};

const PaymentCardActions = ({ account, openCreateAccount, onRemove }: PaymentCardProps) => {
  const { data: paymentAccount } = useSelectPayoutAccount(account);

  const handleRemove = useCallback(() => {
    if (onRemove && paymentAccount) {
      onRemove(paymentAccount.uuid);
    }
  }, [onRemove, paymentAccount]);

  if (!paymentAccount) {
    return (
      <ButtonMantine size="sm" variant="subtle" onClick={() => openCreateAccount(account)}>
        Connect Account
      </ButtonMantine>
    );
  }
  if (onRemove) {
    return (
      <ButtonMantine size="sm" color="cerise" variant="subtle" onClick={handleRemove}>
        Remove Account
      </ButtonMantine>
    );
  }

  return <RadioMantine value={account} />;
};

const PaymentCard = ({ account, openCreateAccount, onRemove }: PaymentCardProps) => {
  const { isLoading } = useSelectPayoutAccount(account);
  const [debouncedLoading] = useDebouncedValue(isLoading, 250);

  return (
    <Card radius="sm">
      <Skeleton visible={debouncedLoading}>
        <Group justify="space-between" gap="sm" wrap="nowrap">
          <Stack gap={4}>
            <Text fz="sm" fw={500}>
              {PAYMENT_ACCOUNTS_DICTIONARY[account].name}
            </Text>
            <PaymentName account={account} />
          </Stack>
          <PaymentCardActions
            account={account}
            openCreateAccount={openCreateAccount}
            onRemove={onRemove}
          />
        </Group>
      </Skeleton>
    </Card>
  );
};

interface PaymentCardsProps
  extends Pick<ChoosePaymentAccountProps, 'openCreateAccount' | 'onRemove'> {}

const PaymentCards = ({ openCreateAccount, onRemove }: PaymentCardsProps) => {
  const form = useForm<ChoosePaymentAccountForm>({
    initialValues: {
      paymentAccount: undefined,
    },
    validate: {
      paymentAccount: isNotEmpty(),
    },
  });
  const handleRemove = useCallback(
    (account: keyof typeof PAYMENT_ACCOUNTS_DICTIONARY, accountId: string) => {
      if (onRemove) {
        onRemove(account, accountId);
      }
    },
    [onRemove]
  );

  useFormListValidator({ id: 'choose_payment_accounts', form: form });
  return (
    <Stack gap="xs">
      <Card bg="gray.0" bd="2px solid gray.0" p={0}>
        <RadioMantine.Group {...form.getInputProps('paymentAccount')}>
          <Stack gap={2}>
            {Object.keys(PAYMENT_ACCOUNTS_DICTIONARY)?.map((key) => (
              <PaymentCard
                key={key}
                account={key as keyof typeof PAYMENT_ACCOUNTS_DICTIONARY}
                openCreateAccount={openCreateAccount}
                onRemove={
                  onRemove
                    ? (accountId: string) =>
                        handleRemove(key as keyof typeof PAYMENT_ACCOUNTS_DICTIONARY, accountId)
                    : undefined
                }
              />
            ))}
          </Stack>
        </RadioMantine.Group>
      </Card>
      <Alert.Collapse
        type="error"
        in={Boolean(form.errors['paymentAccount'])}
        title="Select an account"
      />
    </Stack>
  );
};

const ChoosePaymentAccount = ({
  openCreateAccount,
  onRemove,
  withTitle,
}: ChoosePaymentAccountProps) => {
  return (
    <Stack>
      {withTitle && <Title order={4}>Choose where you want to transfer</Title>}
      <PaymentCards openCreateAccount={openCreateAccount} onRemove={onRemove} />
    </Stack>
  );
};

export { ChoosePaymentAccount };
export type { UseChoosePaymentAccountForm };
